import { useState } from "react";
import { ArrowUp } from "lucide-react";
import { FaInstagram, FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { COMPANY, NAV_LINKS, SOCIALS } from "@/data/content";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import { openWhatsApp } from "@/lib/whatsapp";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const subscribe = (e) => {
    e.preventDefault();
    openWhatsApp(`Hi, please add me to Godavari Builders updates.${email ? ` My email is ${email}.` : ""}`);
    setEmail("");
  };

  const socials = [
    { I: FaInstagram, label: "Instagram", h: SOCIALS.instagram },
    { I: FaLinkedinIn, label: "LinkedIn", h: SOCIALS.linkedinCompany },
    { I: FaFacebookF, label: "Facebook", h: SOCIALS.facebook },
  ];

  return (
    <footer data-testid="footer" className="relative overflow-hidden border-t border-[var(--border-subtle)] bg-[var(--bg-elevated)] pt-16">
      <div className="container-pad">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="font-playfair text-2xl font-bold tracking-[0.04em] text-[var(--text-primary)]">GODAVARI</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)]">Builders &amp; Developers</div>
            <p className="mt-5 max-w-[34ch] text-sm leading-relaxed text-[var(--text-secondary)]">{COMPANY.tagline}.</p>
            <a href={`mailto:${COMPANY.email}`} className="mt-4 inline-block text-sm text-[var(--accent-secondary)] hover:text-[var(--accent-cream)] transition-colors">{COMPANY.email}</a>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--text-muted)]">Navigation</div>
            <ul className="mt-5 flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button onClick={() => scrollToId(l.id)} className="text-sm text-[var(--text-secondary)] transition-colors hover:text-[var(--accent-secondary)]">{l.label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--text-muted)]">Follow Us</div>
            <ul className="mt-5 flex flex-col gap-3">
              {socials.map(({ I, label, h }) => (
                <li key={label}>
                  <a href={h} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-sm text-[var(--text-secondary)] transition-colors hover:text-[var(--accent-secondary)]">
                    <span className="grid h-9 w-9 place-items-center rounded-full border border-[var(--border-subtle)]"><I size={14} /></span>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--text-muted)]">Get Updates</div>
            <form onSubmit={subscribe} className="mt-5">
              <div className="flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.03)] p-1.5">
                <input
                  data-testid="footer-newsletter-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="Your email"
                  className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none"
                />
                <button data-testid="footer-newsletter-submit-button" type="submit" className="shrink-0 rounded-full bg-[image:var(--gradient-cta)] px-4 py-2 text-sm font-semibold text-[#0C0C0C] transition-[filter] hover:brightness-105 focus-ring">
                  Submit &rarr;
                </button>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-[var(--text-muted)]">{COMPANY.address}</p>
            </form>
          </div>
        </div>
      </div>

      <div className="pointer-events-none mt-12 select-none overflow-hidden">
        <div
          className="text-center font-playfair font-extrabold uppercase leading-none"
          style={{ fontSize: "clamp(64px,15vw,220px)", color: "transparent", WebkitTextStroke: "1px rgba(245,230,208,0.10)" }}
        >
          GODAVARI
        </div>
      </div>

      <div className="border-t border-[var(--border-subtle)]">
        <div className="container-pad flex flex-col items-center justify-between gap-4 py-6 text-center md:flex-row md:text-left">
          <span className="text-xs text-[var(--text-muted)]">&copy; {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</span>
          <span className="text-xs text-[var(--text-muted)]">Designed with care in Nashik, Maharashtra &bull; Since 1999</span>
          <button data-testid="footer-back-to-top-button" onClick={scrollToTop} className="inline-flex items-center gap-2 text-xs text-[var(--text-secondary)] transition-colors hover:text-[var(--accent-secondary)]">
            Back to Top <ArrowUp size={14} />
          </button>
        </div>
      </div>

      <div className="border-t border-[var(--border-subtle)] bg-[rgba(255,245,230,0.015)]">
        <div className="container-pad flex flex-col items-center justify-center gap-2 py-5 text-center sm:flex-row sm:gap-3.5">
          <span className="text-xs tracking-[0.02em] text-[var(--text-muted)]">
            Powered by{" "}
            <a
              data-testid="footer-credit-ready2up"
              href="https://ready2up.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-credit font-medium text-[var(--accent-secondary)] transition-colors hover:text-[var(--accent-cream)]"
            >
              Ready2UP
            </a>
          </span>
          <span className="hidden h-3 w-px bg-[var(--border-hover)] sm:inline-block" aria-hidden="true" />
          <span className="text-xs tracking-[0.02em] text-[var(--text-muted)]">
            Designed by{" "}
            <a
              data-testid="footer-credit-dragosaurabh"
              href="https://dragosaurabh.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-credit font-medium text-[var(--accent-secondary)] transition-colors hover:text-[var(--accent-cream)]"
            >
              Dragosaurabh
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};
